/**
 * Custom Domain Detector
 *
 * Detects when the app is accessed via a custom domain and redirects
 * to the appropriate /book/:slug path for that business.
 *
 * This enables white-label custom domains:
 * - example.com → redirects to booker-1d469.web.app/book/example-slug
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-booker-1d469.cloudfunctions.net';

// Default Booker domains (not tenant custom domains)
const DEFAULT_DOMAINS = [
  'booker-1d469.web.app',
  'booker-1d469.firebaseapp.com',
  'localhost',
  // FutureSense subdomains
  'nexus.futuresenseai.com',
  'build.futuresenseai.com',
  'futuresenseai.com'
];

const CustomDomainDetector = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isCustomDomain, setIsCustomDomain] = useState(false);

  useEffect(() => {
    const detectCustomDomain = async () => {
      const hostname = window.location.hostname;

      // Check if it's a default Booker domain
      const isDefault = DEFAULT_DOMAINS.some(domain => hostname.includes(domain));

      if (isDefault) {
        // Not a custom domain - continue normally
        setIsChecking(false);
        setIsCustomDomain(false);
        return;
      }

      // Check if we've already redirected (avoid redirect loop)
      if (location.pathname.startsWith('/book/')) {
        setIsChecking(false);
        setIsCustomDomain(true);
        return;
      }

      try {
        // Call API to resolve custom domain to business slug
        const response = await fetch(
          `${FUNCTIONS_URL}/resolveCustomDomain?hostname=${encodeURIComponent(hostname)}`
        );

        if (!response.ok) {
          // Custom domain not found or not verified
          console.log('Custom domain not found or not verified:', hostname);
          setIsChecking(false);
          setIsCustomDomain(false);
          return;
        }

        const data = await response.json();
        const { businessSlug } = data;

        if (businessSlug) {
          // Redirect to /book/:slug with current path preserved
          const targetPath = `/book/${businessSlug}${location.pathname}${location.search}`;
          console.log('Custom domain detected, redirecting to:', targetPath);
          setIsCustomDomain(true);
          setIsChecking(false); // CRITICAL: Must set false before navigate so children render after redirect
          navigate(targetPath, { replace: true });
        } else {
          setIsChecking(false);
          setIsCustomDomain(false);
        }

      } catch (error) {
        console.error('Error detecting custom domain:', error);
        // On error, just continue normally
        setIsChecking(false);
        setIsCustomDomain(false);
      }
    };

    detectCustomDomain();
  }, []); // Only run once on mount

  // While checking, show nothing (avoids flash of wrong content)
  if (isChecking) {
    return null;
  }

  // Render children once detection is complete
  return children;
};

export default CustomDomainDetector;
